import { Suspense, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Navigate, Outlet, useLocation, useMatches, useNavigate } from "react-router-dom"
import { PageWrapper } from "@/shared/components/common/page-wrapper"
import Spinner from "@/shared/components/common/Spinner"
import { Tab, type TabItem } from "@/shared/components/common/tab"
import { tCommon } from "@/shared/config/i18n"
import { BASE_ROLES } from "@/shared/constants"
import { useCurrentUser } from "@/shared/hooks/use-current-user.ts"
import { getLastPath } from "@/shared/utils"

type ITabPage = "statistics" | "organizations" | "consumers" | "auditors" | "users" | "roles" | "dictionaries"

const allControlTabs: Record<ITabPage, TabItem> = {
  statistics: { label: tCommon("dashboard"), path: "statistics" },
  organizations: {
    label: tCommon("uzEnergyInspection"),
    path: "organizations",
    allowedPermissions: ["organization_view"]
  },
  consumers: {
    label: tCommon("consumers"),
    path: "consumers",
    allowedPermissions: ["company_view"]
  },
  auditors: {
    label: tCommon("auditors"),
    path: "auditors",
    allowedPermissions: ["company_view"]
  },
  users: {
    label: tCommon("users"),
    path: "users",
    allowedPermissions: ["user_view"]
  },
  roles: {
    label: tCommon("roles_text"),
    path: "roles",
    allowedPermissions: ["role_view"],
    disallowedRoles: [BASE_ROLES.REPUBLIC_INSPECTOR!]
  },
  dictionaries: {
    label: tCommon("dictionaries"),
    path: "dictionaries",
    disallowedRoles: [BASE_ROLES.REPUBLIC_INSPECTOR!],
    allowedPermissions: ["soatosource_view", "energyaudittype_view", "energyresource_view"]
  }
}

const Control = () => {
  // Helpers
  const { pathname } = useLocation()
  const { t } = useTranslation("common")
  const navigate = useNavigate()
  const { permissions, base_role } = useCurrentUser()
  const matches = useMatches()
  const last = getLastPath(pathname)
  const isControl = last === "control"

  const tabs = useMemo(
    () =>
      Object.entries(allControlTabs).reduce(
        (acc, [key, value]) => {
          if (value.allowedPermissions) {
            const hasPermission = value.allowedPermissions.some(permission => permissions?.includes(permission))
            const hasDisallowedRole = value.disallowedRoles?.some(role => base_role === role)
            return hasPermission && !hasDisallowedRole
              ? {
                  ...acc,
                  [key]: value
                }
              : acc
          }
          return { ...acc, [key]: value }
        },
        {} as Record<ITabPage, TabItem>
      ),
    [t, permissions]
  )

  const getActiveTab = () => {
    const pathSegments = pathname.split("/").filter(Boolean)
    const basePath = pathSegments[pathSegments.indexOf("control") + 1]
    return basePath in tabs ? tabs[basePath as ITabPage] : tabs.organizations
  }
  // States
  const [tab, setTab] = useState(tabs.organizations)
  const [hideTabs, setHideTabs] = useState(true)

  const currentMatch = matches[matches.length - 1]
  const routeHandle = currentMatch?.handle as any

  // Effects
  useEffect(() => {
    setTab(getActiveTab())
    setHideTabs(routeHandle?.hideTabs)
  }, [pathname, routeHandle, tabs, getActiveTab])

  // Functions
  const onTabChange = useCallback(
    (newTab: TabItem) => {
      setTab(newTab)
      navigate(`/dashboard/control/${newTab.path}`)
    },
    [navigate, setTab]
  )

  const tabValues = useMemo(() => Object.values(tabs), [tabs])
  const tab2 = useMemo(() => tab, [tab])

  if (hideTabs) return <Outlet />
  if (isControl) return <Navigate to={Object.keys(tabs)[0]} />

  return (
    <PageWrapper>
      <Tab spacing={20} size="sm" tabs={tabValues} value={tab2} onChange={onTabChange} />
      <Suspense fallback={<Spinner className="!bg-background" />}>
        <Outlet />
      </Suspense>
    </PageWrapper>
  )
}

export default Control
